// Разбор `actionUrl` уведомления в аргументы TanStack Router.
//
// Зачем отдельный хелпер, а не `navigate({ to: url })`:
// TanStack Router собирает pathname через `resolvePathWithBase(to)` и НЕ
// разбирает `?…` / `#…` внутри строки — query уезжает В ИМЯ ПУТИ, и переход
// заканчивается Not Found. Сервер же кладёт в `actionUrl` полноценные ссылки
// с параметрами: сцена воркспейса (`/courses/$id/topics/$topicId?view=note`,
// декрет E35), diff правовой версии (`/legal/accept?diff=…`), якорь чанка.
//
// Хелпер делит строку на `to` / `search` / `hash`, чтобы роутер получил их
// по отдельности. Внешние URL (`http(s)://`) сюда не попадают — их обрабатывает
// вызывающий код полной навигацией.

export interface ParsedActionUrl {
	/** Чистый pathname или ID роута (`/courses/$courseId/topics/$topicId`). */
	to: string;
	/** Path-параметры, если `to` — ID роута, а не интерполированный путь. */
	params?: Record<string, string>;
	/** Query-параметры, если были. */
	search?: Record<string, string>;
	/** Хеш без `#`, если был. */
	hash?: string;
}

/** Статические сегменты `/courses/*`, это не `$courseId` (как в `_layout-state`). */
const COURSE_STATIC_SEGMENTS = new Set(["calendar", "catalog", "templates"]);

/**
 * Воркспейс курса объявлен non-nested-файлом `$courseId_.topics.$topicId`
 * (декрет E9). Ссылки внутри приложения идут через ID роута + `params`
 * (`topicLinkProps`). Интерполированный `/courses/{uuid}/topics/{uuid}` с
 * отдельным `search` на первом переходе с другого роута (дашборд → уведомление)
 * может приехать без `?view=` — и каркас откроет шаг по спине (часто ②).
 */
function rewriteCourseWorkspacePath(to: string): Pick<ParsedActionUrl, "to" | "params"> {
	const topic = /^\/courses\/([^/]+)\/topics\/([^/]+)\/?$/.exec(to);
	const topicCourseId = topic?.[1];
	const topicId = topic?.[2];
	if (topicCourseId && topicId) {
		return {
			to: "/courses/$courseId/topics/$topicId",
			params: { courseId: topicCourseId, topicId },
		};
	}
	const course = /^\/courses\/([^/]+)\/?$/.exec(to);
	const courseId = course?.[1];
	if (courseId && !COURSE_STATIC_SEGMENTS.has(courseId)) {
		return { to: "/courses/$courseId", params: { courseId } };
	}
	return { to };
}

/** `true` для абсолютных внешних ссылок — их SPA-навигация не обслуживает. */
export function isExternalUrl(url: string): boolean {
	return /^https?:\/\//.test(url);
}

export function parseActionUrl(url: string): ParsedActionUrl {
	const hashAt = url.indexOf("#");
	const hash = hashAt >= 0 ? url.slice(hashAt + 1) : "";
	const withoutHash = hashAt >= 0 ? url.slice(0, hashAt) : url;

	const queryAt = withoutHash.indexOf("?");
	const to = queryAt >= 0 ? withoutHash.slice(0, queryAt) : withoutHash;
	const query = queryAt >= 0 ? withoutHash.slice(queryAt + 1) : "";

	const search: Record<string, string> = {};
	if (query) {
		for (const [key, value] of new URLSearchParams(query)) search[key] = value;
	}

	const path = rewriteCourseWorkspacePath(to);
	return {
		...path,
		...(Object.keys(search).length > 0 ? { search } : {}),
		...(hash ? { hash } : {}),
	};
}
